// src/hrms/alarms/AlarmRingDialog.tsx
import { useEffect, useState, useSyncExternalStore } from "react";
import { AlarmClock, BellRing, Briefcase, Check, Clock, ExternalLink, Flag, Loader2, NotebookText, Volume2, VolumeX } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { cn } from "../lib/utils";
import {
  acknowledgeOpened,
  alarmItemPath,
  completeFromAlarm,
  dismissAlarm,
  remindAgain,
  snoozeAlarm,
  type CompletionResult,
} from "./alarmActions";
import { priorityLabel } from "./alarmModel";
import { parseAlarmDateTime, type ActiveOccurrence } from "./alarmSchedule";
import { alarmAudioReady, subscribeAlarmAudio, unlockAlarmAudio } from "./alarmSound";
import { durationLabel, REMIND_AGAIN_OPTIONS } from "./alarmTypes";

function clock(epochMs: number) {
  return new Date(epochMs).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
}

function dayAndClock(epochMs: number, now: number) {
  const date = new Date(epochMs);
  if (date.toDateString() === new Date(now).toDateString()) return `today, ${clock(epochMs)}`;
  return `${date.toLocaleDateString(undefined, { day: "numeric", month: "short", weekday: "short" })}, ${clock(epochMs)}`;
}

export function completionToast(result: CompletionResult) {
  switch (result.kind) {
    case "completed":
      toast.success("Marked complete");
      return;
    case "sent-for-review":
      toast.success("Sent for review");
      return;
    case "queued":
      toast.message("You're offline", { description: "It will be marked complete when you're back online." });
      return;
    case "refused":
      toast.error(result.message);
  }
}

/**
 * The in-page alarm: what is ringing (or was missed), and the five answers.
 * It is a modal with no close button and no Escape: an alarm waits for an
 * answer, and Dismiss is one click away. Several at once queue, oldest first.
 */
export function AlarmRingDialog({
  entries,
  mode,
  now,
  onClose,
  onNavigate,
  ownerId,
}: {
  entries: ActiveOccurrence[];
  mode: "ringing" | "missed";
  now: number;
  /** Missed alarms only: put the list away without answering. */
  onClose?: () => void;
  onNavigate: (path: string) => void;
  ownerId: string;
}) {
  const current = entries[0];
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [remindOpen, setRemindOpen] = useState(false);
  const soundReady = useSyncExternalStore(subscribeAlarmAudio, alarmAudioReady, alarmAudioReady);
  const currentId = current ? `${current.itemKey}|${current.occurrence.id}` : "";

  useEffect(() => {
    setError(null);
    setRemindOpen(false);
  }, [currentId]);

  if (!current) return null;
  const { alarm, itemKey, occurrence } = current;
  const missed = mode === "missed";
  const due = parseAlarmDateTime(alarm.item.dueAt);
  const eyebrow = missed ? "Missed alarm" : occurrence.reason === "snooze" ? "Snoozed alarm" : occurrence.reason === "remind" ? "Reminder" : "Task alarm";

  const complete = async () => {
    setBusy("complete");
    setError(null);
    try {
      const result = await completeFromAlarm(ownerId, itemKey, occurrence.id);
      if (result.kind === "refused") setError(result.message);
      else completionToast(result);
    } finally {
      setBusy(null);
    }
  };

  const open = () => {
    acknowledgeOpened(ownerId, itemKey, occurrence.id);
    onNavigate(alarmItemPath(itemKey));
  };

  return (
    <Dialog open onOpenChange={(next) => !next && onClose?.()}>
      <DialogContent
        className={cn("gt-alarm-ring", missed && "is-missed")}
        showClose={Boolean(onClose)}
        onEscapeKeyDown={(event) => {
          if (!onClose) event.preventDefault();
        }}
        onPointerDownOutside={(event) => event.preventDefault()}
        onInteractOutside={(event) => event.preventDefault()}
        aria-describedby={undefined}
      >
        <div className="gt-alarm-ring__top">
          <span className="gt-alarm-ring__eyebrow">
            {missed ? <AlarmClock aria-hidden="true" /> : <BellRing aria-hidden="true" className="gt-alarm-ring__bell" />}
            {eyebrow}
          </span>
          {entries.length > 1 && (
            <span className="gt-alarm-ring__count" aria-label={`Alarm 1 of ${entries.length}`}>
              1 of {entries.length}
            </span>
          )}
        </div>

        <div className="gt-alarm-ring__clock" aria-hidden="true">{clock(occurrence.at)}</div>
        <DialogTitle className="gt-alarm-ring__title">{alarm.item.title}</DialogTitle>
        {alarm.item.description ? (
          <DialogDescription className="gt-alarm-ring__desc">{alarm.item.description}</DialogDescription>
        ) : null}
        <ul className="gt-alarm-ring__meta">
          <li>
            <Clock aria-hidden="true" /> {due === null ? "No due date" : `Due ${dayAndClock(due, now)}`}
          </li>
          <li>
            <Flag aria-hidden="true" /> {priorityLabel(alarm.item.priority)} priority
          </li>
          <li>
            {alarm.item.source === "todo" ? <NotebookText aria-hidden="true" /> : <Briefcase aria-hidden="true" />}
            {alarm.item.source === "todo" ? "Planner" : "Assigned task"}
          </li>
        </ul>

        {!missed && !soundReady && (
          <div className="gt-alarm-ring__sound">
            <VolumeX aria-hidden="true" />
            <span>Your browser is holding back the alarm sound until you interact with the page.</span>
            <Button type="button" size="sm" variant="outline" onClick={unlockAlarmAudio}>
              <Volume2 aria-hidden="true" /> Turn on sound
            </Button>
          </div>
        )}

        {error && (
          <p role="alert" className="gt-alarm-ring__error">
            {error}
          </p>
        )}

        <div className="gt-alarm-ring__actions">
          <Button type="button" className="gt-alarm-ring__primary" onClick={() => void complete()} disabled={busy !== null} aria-busy={busy === "complete"}>
            {busy === "complete" ? <Loader2 aria-hidden="true" className="animate-spin" /> : <Check aria-hidden="true" />} Complete
          </Button>
          {!missed && (
            <Button type="button" variant="secondary" disabled={busy !== null} onClick={() => snoozeAlarm(ownerId, itemKey, occurrence.id)}>
              Snooze {alarm.snoozeMinutes} min
            </Button>
          )}
          <Button type="button" variant="secondary" aria-expanded={remindOpen} disabled={busy !== null} onClick={() => setRemindOpen((value) => !value)}>
            Remind me again
          </Button>
          {remindOpen && (
            <div className="gt-alarm-ring__remind" role="group" aria-label="Remind me again in">
              {REMIND_AGAIN_OPTIONS.map((minutes) => (
                <Button
                  key={minutes}
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    remindAgain(ownerId, itemKey, occurrence.id, Date.now() + minutes * 60_000);
                    toast.success(`We'll remind you in ${durationLabel(minutes)}`);
                  }}
                >
                  In {durationLabel(minutes)}
                </Button>
              ))}
            </div>
          )}
          <Button type="button" variant="outline" disabled={busy !== null} onClick={open}>
            <ExternalLink aria-hidden="true" /> {alarm.item.source === "todo" ? "Open planner" : "Open task"}
          </Button>
          <Button type="button" variant="ghost" disabled={busy !== null} onClick={() => dismissAlarm(ownerId, itemKey, occurrence.id)}>
            Dismiss
          </Button>
        </div>
        <p className="gt-alarm-ring__footnote">Dismiss stops this alarm only. The {alarm.item.source === "todo" ? "item" : "task"} stays open.</p>
        {missed && onClose && (
          <Button type="button" variant="link" size="sm" className="gt-alarm-ring__later" onClick={onClose}>
            Decide later
          </Button>
        )}
      </DialogContent>
    </Dialog>
  );
}
